import { Transforms, Path, Node, Editor, Range, Point } from 'slate';

/**
 * Plugin for embedded media: treats images/embeds as void elements
 * @param {Editor} editor - Slate editor instance
 * @returns {Editor} Extended editor with embed handling
 */
const withEmbeds = (editor) => {
    const { isVoid, insertBreak, deleteBackward, deleteForward } = editor;

    editor.isVoid = (element) => (['video', 'image', 'htmlCode'].includes(element.type) ? true : isVoid(element));

    editor.insertBreak = (...args) => {
        const parentPath = Path.parent(editor.selection.focus.path);
        const parentNode = Node.get(editor, parentPath);
        if (editor.isVoid(parentNode)) {
            const nextPath = Path.next(parentPath);
            Transforms.insertNodes(
                editor,
                {
                    type: 'paragraph',
                    children: [{ text: '' }],
                },
                {
                    at: nextPath,
                    select: true, // Focus on this node once inserted
                },
            );
        } else {
            insertBreak(...args);
        }
    };

    editor.deleteBackward = (unit) => {
        const { selection } = editor;
        if (selection && Range.isCollapsed(selection)) {
            const [parentNode, parentPath] = Editor.parent(editor, selection);
            // If cursor is inside a void element, remove it
            if (editor.isVoid(parentNode)) {
                Transforms.removeNodes(editor, { at: parentPath });
                return;
            }
            // If cursor is at the start of a block and the previous node is void, remove that void
            const start = Editor.start(editor, parentPath);
            if (Point.equals(selection.anchor, start)) {
                const prevEntry = Editor.previous(editor, { at: parentPath });
                if (prevEntry) {
                    const [prevNode] = prevEntry;
                    if (editor.isVoid(prevNode)) {
                        Transforms.removeNodes(editor, { at: prevEntry[1] });
                        return;
                    }
                }
            }
        }
        deleteBackward(unit);
    };

    editor.deleteForward = (unit) => {
        const { selection } = editor;
        if (selection && Range.isCollapsed(selection)) {
            const [parentNode, parentPath] = Editor.parent(editor, selection);
            if (editor.isVoid(parentNode)) {
                Transforms.removeNodes(editor, { at: parentPath });
                return;
            }
            // If cursor is at the end of a block and the next node is void, remove that void
            const end = Editor.end(editor, parentPath);
            if (Point.equals(selection.anchor, end)) {
                const nextEntry = Editor.next(editor, { at: parentPath });
                if (nextEntry) {
                    const [nextNode] = nextEntry;
                    if (editor.isVoid(nextNode)) {
                        Transforms.removeNodes(editor, { at: nextEntry[1] });
                        return;
                    }
                }
            }
        }
        deleteForward(unit);
    };

    return editor;
};

export default withEmbeds;
